/* Devocione V7.2 — visible-only, muted original clips and a compact native dialog. */
(() => {
  'use strict';
  if (customElements.get('dv7-community')) return;
  const modified = event => event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  class Community extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController(); const options = { signal: this.controller.signal };
      this.rail = this.querySelector('[data-dv7-rail]'); if (!this.rail) return;
      this.cards = [...this.querySelectorAll('[data-dv7-card]')];
      this.previews = [...this.querySelectorAll('[data-dv7-preview]')];
      this.previous = this.querySelector('[data-dv7-prev]'); this.next = this.querySelector('[data-dv7-next]');
      this.count = this.querySelector('[data-dv7-count]'); this.dialog = this.querySelector('[data-dv7-video-dialog]');
      this.playerHost = this.querySelector('[data-dv7-video-player]'); this.toggle = this.querySelector('[data-dv7-preview-toggle]');
      this.motion = matchMedia('(prefers-reduced-motion:reduce)'); this.previewPaused = false; this.explicitPlay = false;
      this.visiblePreviews = new Set();
      this.querySelector('[data-dv7-rail-buttons]')?.removeAttribute('hidden');
      if (this.toggle) { this.toggle.hidden = !this.previews.length; this.toggle.addEventListener('click', () => {
        const playing = this.previews.some(video => !video.paused);
        this.previewPaused = playing; this.explicitPlay = !playing; this.refreshPreviews();
      }, options); }
      const move = direction => {
        const card = this.cards[0]; if (!card) return;
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
        this.rail.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: this.motion.matches ? 'instant' : 'smooth' });
      };
      this.previous?.addEventListener('click', () => move(-1), options);
      this.next?.addEventListener('click', () => move(1), options);
      this.rail.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame); this.scrollFrame = requestAnimationFrame(() => this.refresh());
      }, { ...options, passive: true });
      this.rail.addEventListener('keydown', event => {
        if (event.target !== this.rail) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
        if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); this.rail.scrollTo({ left: event.key === 'Home' ? 0 : this.rail.scrollWidth, behavior: 'instant' }); }
      }, options);
      if ('ResizeObserver' in window) { this.resizeObserver = new ResizeObserver(() => this.refresh()); this.resizeObserver.observe(this.rail); }
      this.previews.forEach(video => {
        video.muted = true; video.defaultMuted = true;
        video.addEventListener('playing', () => { video.classList.add('is-playing'); this.refreshToggle(); }, options);
        video.addEventListener('pause', () => this.refreshToggle(), options);
        video.addEventListener('error', () => { video.classList.remove('is-playing'); video.dataset.failed = 'true'; }, options);
      });
      if ('IntersectionObserver' in window) {
        this.videoObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.isIntersecting && entry.intersectionRatio >= 0.25) this.visiblePreviews.add(entry.target); else this.visiblePreviews.delete(entry.target); });
          this.refreshPreviews();
        }, { threshold: [0, 0.25, 0.6] });
        this.previews.forEach(video => this.videoObserver.observe(video));
      } else {
        // Progressive fallback: a first visible card only, never download the entire rail.
        if (this.previews[0]) this.visiblePreviews.add(this.previews[0]);
      }
      this.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('[data-dv7-video-close]')) { this.dialog?.close(); return; }
        const link = event.target.closest('[data-dv7-video]');
        if (!link || modified(event) || typeof this.dialog?.showModal !== 'function') return;
        const template = this.querySelector(`template[id="${CSS.escape(link.dataset.dv7Video)}"]`);
        if (!(template instanceof HTMLTemplateElement)) return;
        try {
          this.stopPlayer(); this.playerHost.replaceChildren(template.content.cloneNode(true));
          const video = this.playerHost.querySelector('video'); if (!video) return;
          const captions = this.playerHost.querySelector('[data-dv7-captions-url]');
          if (captions) {
            const track = document.createElement('track'); track.kind = 'captions'; track.label = 'Subtitles';
            track.srclang = captions.dataset.dv7CaptionsLanguage || 'en'; track.src = captions.dataset.dv7CaptionsUrl;
            video.appendChild(track); captions.remove();
          }
          video.playsInline = true;
          this.querySelector('[data-dv7-video-title]').textContent = template.dataset.dv7ClipTitle || 'Video de la comunidad';
          this.opener = link; this.dialog.showModal(); event.preventDefault();
          document.documentElement.classList.add('dv7-video-open');
          document.dispatchEvent(new CustomEvent('dv7:video:state', { detail: { open: true } }));
          this.querySelector('[data-dv7-video-close]')?.focus({ preventScroll: true });
          const fallback = () => {
            if (!this.dialog.open || this.playerHost.querySelector('.dv7-video-error')) return;
            const a = document.createElement('a'); a.className = 'dv7-video-error'; a.href = link.href;
            a.textContent = 'Video not playing? Open the original video.'; a.target = '_blank'; a.rel = 'noopener noreferrer'; this.playerHost.appendChild(a);
          };
          video.addEventListener('error', fallback, { once: true });
          this.playerTimer = setTimeout(() => { if (video.readyState < 2) fallback(); }, 9000);
          video.addEventListener('playing', () => { clearTimeout(this.playerTimer); this.playerHost.querySelector('.dv7-video-error')?.remove(); }, { once: true });
          // No network await before play: retain the user's iOS activation gesture.
          video.play()?.catch(() => { /* Native play/sound controls remain available. */ });
        } catch (_) { this.stopPlayer(); if (this.dialog.open) this.dialog.close(); }
      }, options);
      this.dialog?.addEventListener('close', () => this.finishVideo(), options);
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const b = this.dialog.getBoundingClientRect();
        if (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom) this.dialog.close();
      }, options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.playerHost.querySelector('video')?.pause(); this.refreshPreviews(); }, options);
      document.addEventListener('dv7:video:state', () => this.refreshPreviews(), options);
      this.motion.addEventListener('change', () => { this.explicitPlay = false; this.refreshPreviews(); }, options);
      document.addEventListener('shopify:block:select', event => {
        const card = this.cards.find(item => item.dataset.blockId === event.detail?.blockId);
        if (card) this.rail.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: 'instant' });
      }, options);
      this.refresh(); this.refreshPreviews();
    }
    refresh() {
      const box = this.rail.getBoundingClientRect();
      const visible = this.cards.map((card, i) => ({ i, box: card.getBoundingClientRect() })).filter(item => item.box.right > box.left + 12 && item.box.left < box.right - 12);
      if (this.previous) this.previous.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 3;
      if (this.count) {
        const first = visible[0]?.i, last = visible.at(-1)?.i;
        this.count.textContent = visible.length ? `${first + 1}${last === first ? '' : `–${last + 1}`} / ${this.cards.length}` : '';
      }
    }
    refreshToggle() {
      if (!this.toggle) return;
      const paused = this.previewPaused || (!this.explicitPlay && (this.motion.matches || this.dataset.autoplayPreviews === 'false'));
      this.toggle.textContent = paused ? 'Reproducir videos' : 'Pausar videos'; this.toggle.setAttribute('aria-pressed', String(paused));
    }
    refreshPreviews() {
      const shouldPlay = !this.previewPaused && (this.explicitPlay || (this.dataset.autoplayPreviews !== 'false' && !this.motion.matches)) && !document.hidden && !document.querySelector('[data-dv7-video-dialog][open]');
      this.previews.forEach(video => {
        if (!shouldPlay || !this.visiblePreviews.has(video) || video.dataset.failed) { video.pause(); return; }
        if (!video.dataset.loaded) {
          video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
          video.dataset.loaded = 'true'; video.preload = 'metadata'; video.load();
        }
        if (video.paused) { video.muted = true; video.play()?.catch(() => { /* Poster + original-video link remain usable. */ }); }
      });
      this.refreshToggle();
    }
    stopPlayer() {
      clearTimeout(this.playerTimer);
      this.playerHost?.querySelectorAll('video').forEach(video => { video.pause(); video.removeAttribute('src'); video.querySelectorAll('source').forEach(source => source.removeAttribute('src')); video.load(); });
      this.playerHost?.replaceChildren();
    }
    finishVideo() {
      this.stopPlayer();
      if (!document.querySelector('[data-dv7-video-dialog][open]')) document.documentElement.classList.remove('dv7-video-open');
      document.dispatchEvent(new CustomEvent('dv7:video:state', { detail: { open: false } }));
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true }); this.opener = null;
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.resizeObserver?.disconnect(); this.videoObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame); this.previews?.forEach(video => video.pause());
      if (this.dialog?.open) this.dialog.close(); this.finishVideo();
    }
  }
  customElements.define('dv7-community', Community);
})();
